import React, { useState, useEffect } from 'react';
import { Tag, Button, Select, Spin, Modal, Row, Col, Empty } from 'antd';
import { EyeOutlined, CloseCircleOutlined, CalendarOutlined, CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment';
import API from '../../services';
import HeaderFarms from '../../utils/header_farms';
import { Report } from 'notiflix';

const { Option } = Select;

function MyRequests({ 
  instance, 
  loginRequest, 
  accounts, 
  userInfo,
  carList
}) {
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filters, setFilters] = useState({
    plate_number: '',
    status: ''
  });

  useEffect(() => {
    if (userInfo?.employee_id) {
      loadMyRequests();
    }
  }, [userInfo]);

  const loadMyRequests = async () => {
    
    setLoading(true);
    try {
      const request = {
        ...loginRequest,
        account: accounts[0],
      };
      const response = await instance.acquireTokenSilent(request);
      const result = await API.ListMyRequests(
        HeaderFarms(response.accessToken),
        userInfo.employee_id,
        filters.plate_number
      );
      setMyRequests(result.data || []);
    } catch (error) {
      console.error('Failed to load my requests:', error);
      Report.warning('Error', 'Failed to load booking requests', 'OK');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (record) => {
  
    Modal.confirm({
      title: 'Cancel Booking',
      content: 'Are you sure you want to cancel this booking?',
      okText: 'Yes, Cancel',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const request = {
            ...loginRequest,
            account: accounts[0],
          };
          const response = await instance.acquireTokenSilent(request);
          
          await API.ApprovalCancel(HeaderFarms(response.accessToken), {
            id: record.approval.data._id,
            msg: 'Cancelled by user'
          });
          
          Report.success('Success', 'Booking cancelled successfully', 'OK');
          loadMyRequests();
        } catch (error) {
          console.error('Failed to cancel booking:', error);
          Report.failure('Error', error.message || 'Failed to cancel booking', 'OK');
        }
      }
    });
  };

  const filteredData = myRequests.filter(item => {
    if (filters.status && item.approval?.data?.status !== filters.status) {
      return false;
    }
    return true;
  });

  const getStatusColor = (status) => {
    const colors = {
      'Waiting Approval': 'orange',
      'Approved': 'green',
      'Reject': 'red',
      'Cancel': 'gray',
    };
    return colors[status] || 'default';
  };

  const groupByDate = (data) => {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'days').startOf('day');
    const weekStart = moment().startOf('week');

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    data.forEach(item => {
      const itemDate = moment(item.date_from).startOf('day');
      
      if (itemDate.isSame(today, 'day')) {
        groups.today.push(item);
      } else if (itemDate.isSame(yesterday, 'day')) {
        groups.yesterday.push(item);
      } else if (itemDate.isSameOrAfter(weekStart)) {
        groups.thisWeek.push(item);
      } else {
        groups.older.push(item);
      }
    });

    return groups;
  };

  const groupedData = groupByDate(filteredData);

  return (
    <div className="container white-back-cr" style={{ padding: '20px', minHeight:500 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder="Filter by Car"
            style={{ width: '100%' }}
            allowClear
            value={filters.plate_number || undefined}
            onChange={(value) => {
              setFilters({ ...filters, plate_number: value });
              setTimeout(() => loadMyRequests(), 100);
            }}
          >
            {carList.map((car) => (
              <Option key={car.plate_number} value={car.plate_number}>
                {car.plate_number} {car.brand && `- ${car.brand}`}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder="Filter by Status"
            style={{ width: '100%' }}
            allowClear
            value={filters.status || undefined}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value="Waiting Approval">Waiting Approval</Option>
            <Option value="Approved">Approved</Option>
            <Option value="Reject">Reject</Option>
            <Option value="Cancel">Cancel</Option>
          </Select>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Button type="primary" onClick={loadMyRequests} block>
            Refresh
          </Button>
        </Col>
      </Row>

      <Spin spinning={loading}>
        {filteredData.length === 0 ? (
          <Empty />
        ) : (
          <>
            {groupedData.today.length > 0 && (
              <>
                <h5 style={{ marginBottom: 16, color: '#1890ff' }}>Today ({groupedData.today.length})</h5>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {groupedData.today.map((item) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={item.id_record}>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#fff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 12 }}>
                            <Tag color={getStatusColor(item.approval?.data?.status)}>
                              {item.approval?.data?.status || 'Unknown'}
                            </Tag>
                          </div>
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
                            {item.id_record}
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <CalendarOutlined style={{ marginRight: 8 }} />
                            <strong>{moment(item.date_from).format('DD/MM/YY HH:mm')}</strong>
                            {' - '}
                            <strong>{moment(item.date_to).format('HH:mm')}</strong>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <CarOutlined style={{ marginRight: 8 }} />
                            {item.plate_number}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                            {item.destination}
                          </div>
                          {item.driver_name && (
                            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                              Driver: {item.driver_name}
                            </div>
                          )}
                        </div>
                        <div style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          marginTop: '12px',
                          display: 'flex',
                          justifyContent: 'space-around'
                        }}>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedBooking(item);
                              setDetailVisible(true);
                            }}
                          >
                            View
                          </Button>
                          {item.approval?.data?.status === 'Waiting Approval' && (
                            <Button
                              type="link"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={() => handleCancel(item)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {groupedData.yesterday.length > 0 && (
              <>
                <h5 style={{ marginBottom: 16, color: '#52c41a' }}>Yesterday ({groupedData.yesterday.length})</h5>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {groupedData.yesterday.map((item) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={item.id_record}>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#fff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 12 }}>
                            <Tag color={getStatusColor(item.approval?.data?.status)}>
                              {item.approval?.data?.status || 'Unknown'}
                            </Tag>
                          </div>
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
                            {item.id_record}
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <CalendarOutlined style={{ marginRight: 8 }} />
                            <strong>{moment(item.date_from).format('DD/MM/YY HH:mm')}</strong>
                            {' - '}
                            <strong>{moment(item.date_to).format('HH:mm')}</strong>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <CarOutlined style={{ marginRight: 8 }} />
                            {item.plate_number}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                            {item.destination}
                          </div>
                          {item.driver_name && (
                            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                              Driver: {item.driver_name}
                            </div>
                          )}
                        </div>
                        <div style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          marginTop: '12px',
                          display: 'flex',
                          justifyContent: 'space-around'
                        }}>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedBooking(item);
                              setDetailVisible(true);
                            }}
                          >
                            View
                          </Button>
                          {item.approval?.data?.status === 'Waiting Approval' && (
                            <Button
                              type="link"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={() => handleCancel(item)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {groupedData.thisWeek.length > 0 && (
              <>
                <h5 style={{ marginBottom: 16, color: '#faad14' }}>This Week ({groupedData.thisWeek.length})</h5>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {groupedData.thisWeek.map((item) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={item.id_record}>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#fff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 12 }}>
                            <Tag color={getStatusColor(item.approval?.data?.status)}>
                              {item.approval?.data?.status || 'Unknown'}
                            </Tag>
                          </div>
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
                            {item.id_record}
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <CalendarOutlined style={{ marginRight: 8 }} />
                            <strong>{moment(item.date_from).format('DD/MM/YY HH:mm')}</strong>
                            {' - '}
                            <strong>{moment(item.date_to).format('HH:mm')}</strong>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <CarOutlined style={{ marginRight: 8 }} />
                            {item.plate_number}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                            {item.destination}
                          </div>
                          {item.driver_name && (
                            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                              Driver: {item.driver_name}
                            </div>
                          )}
                        </div>
                        <div style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          marginTop: '12px',
                          display: 'flex',
                          justifyContent: 'space-around'
                        }}>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedBooking(item);
                              setDetailVisible(true);
                            }}
                          >
                            View
                          </Button>
                          {item.approval?.data?.status === 'Waiting Approval' && (
                            <Button
                              type="link"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={() => handleCancel(item)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {groupedData.older.length > 0 && (
              <>
                <h5 style={{ marginBottom: 16, color: '#8c8c8c' }}>Older ({groupedData.older.length})</h5>
                <Row gutter={[16, 16]}>
                  {groupedData.older.map((item) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={item.id_record}>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#fff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 12 }}>
                            <Tag color={getStatusColor(item.approval?.data?.status)}>
                              {item.approval?.data?.status || 'Unknown'}
                            </Tag>
                          </div>
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
                            {item.id_record}
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <CalendarOutlined style={{ marginRight: 8 }} />
                            <strong>{moment(item.date_from).format('DD/MM/YY HH:mm')}</strong>
                            {' - '}
                            <strong>{moment(item.date_to).format('HH:mm')}</strong>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <CarOutlined style={{ marginRight: 8 }} />
                            {item.plate_number}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                            {item.destination}
                          </div>
                          {item.driver_name && (
                            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                              Driver: {item.driver_name}
                            </div>
                          )}
                        </div>
                        <div style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          marginTop: '12px',
                          display: 'flex',
                          justifyContent: 'space-around'
                        }}>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedBooking(item);
                              setDetailVisible(true);
                            }}
                          >
                            View
                          </Button>
                          {item.approval?.data?.status === 'Waiting Approval' && (
                            <Button
                              type="link"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={() => handleCancel(item)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </>
        )}
      </Spin>

      <Modal
        title="Booking Detail"
        visible={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedBooking && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color={getStatusColor(selectedBooking.approval?.data?.status)}>
                {selectedBooking.approval?.data?.status || 'Unknown'}
              </Tag>
            </div>
            <p><strong>ID:</strong> {selectedBooking.id_record}</p>
            <p><strong>Car:</strong> {selectedBooking.plate_number}</p>
            <p><strong>Destination:</strong> {selectedBooking.destination}</p>
            <p><strong>Objective:</strong> {selectedBooking.objective}</p>
            <p><strong>Date From:</strong> {moment(selectedBooking.date_from).format('DD/MM/YYYY HH:mm')}</p>
            <p><strong>Date To:</strong> {moment(selectedBooking.date_to).format('DD/MM/YYYY HH:mm')}</p>
            {selectedBooking.driver_name && (
              <p><strong>Driver:</strong> {selectedBooking.driver_name}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default MyRequests;
